/**
 * Mixin that manages the retrieval and management of data for the dashboards.
 *
 * This mixin centralizes the logic for retrieving data related to the metrics of a dashboard.
 * The data is stored in a reactive object called `resultsSet`, where each key is the queryId associated with the metric and the value is the result of the query.
 * The result of each query is stored as an array of objects, where each object represents a record (or a row) of the dataset returned by the query.
 * Each object contains the fields and values associated with that particular row of the result.
 *
 * Query behavior:
 * - Metrics are iterated to identify the queries to execute.
 * - If the metric contains an `api` configuration, this means that the data is returned from an external API to ARPA (still experimental, e.g., Superset).
 * - If the metric uses an SQL query, a request is built based on the queryId associated with the metric and is executed via the `fetchData` function.
 * - Each query is associated with a `queryId` that serves as a key within `resultsSet` to map the results of the query.
 */

import { mapActions } from 'vuex'
import Vue from 'vue'
import Configuration from '@/agilis/util/Configuration'
import supersetMixin from '@/agilis/components/Dashboard/mixin/supersetMixin'
// Mixin providing common render utils
export default {
  name: 'dataMixin',

  props: {
    context: Object
  },
  mixins: [supersetMixin],

  data () {
    return {
      resultsSet: {}
    }
  },

  computed: {
    metrics () {
      return this.context?.dashboard?.metrics
    },

    applicationId () {
      return this.context?.applicationId
    },

    isAppDashboard () {
      return !this.context?.workspace
    }
  },

  async created () {
    await this.fetchMetrics()
  },

  methods: {
    ...mapActions({
      'notifyError': 'notificationStore/notifyError',
      'startLoader': 'loaderStore/startLoader',
      'stopLoader': 'loaderStore/stopLoader'
    }),

    /**
     * This method is responsible for collecting and managing the data of the metrics configured for a dashboard.
     *
     * Structure of `resultsSet`:
     * - Each query is associated with an `apiId` (or queryId), which acts as a key in `resultsSet`.
     * - The data returned from the query or API is stored as an array of objects under this key.
     *
     * e.g.
     * {
     *   "exampleQuery": [
     *     {
     *       "id": 1,
     *       "title": "Test Dashboard 1",
     *       -- other fields --
     *     },
     *     {
     *       "id": 2,
     *       "title": "Test Dashboard ",
     *       -- other fields --
     *     }
     *   ]
     * }
     * The update of `resultsSet` is automatic thanks to Vue's reactivity, and any component referencing this data
     * will automatically receive updates when the data is refreshed.
     */
    async fetchMetrics () {
      let results = {}
      let queries = []
      let apis = []
      for (const metricKey in this.metrics) {
        const metric = this.metrics[metricKey]
        const apiId = this.queryId(metric)
        if (metric.api) {
          if (!apis.includes(apiId)) {
            apis.push(apiId)
            if (metric.api) {
              const response = await this.fetchDataApi(metric)
              // if a resultMapperMethod exists in the configuration, it calls a mapping method defined in supersetMixin.js
              if (metric.api?.resultMapperMethod) {
                results[apiId] = this[metric.api?.resultMapperMethod](response)
              }
              Vue.set(this.resultsSet, apiId, results[apiId].items)
            }
          }
        } else {
          if (!queries.includes(apiId)) {
            queries.push(apiId)
            this.fetchData(metric).then((response) => {
              results[apiId] = response
              Vue.set(this.resultsSet, apiId, results[apiId].items)
            })
          }
        }
      }
    },
    async fetchDataApi (metric) {
      if (metric.api) {
        try {
          const config = this.$axios.agilisRequestConfig(metric.api.url)
          const response = await this.$axios.get(metric.api.url, config)
          return response.data
        } catch (error) {
          this.notifyError(error)
        }
      }
    },
    /**
     * Method triggered when the user clicks on the icon to view details in the DashboardCounter component.
     *
     * This method is responsible for fetching the data related to a specific metric when the details are requested.
     * It sends the query, the sorting criteria (`sortBy`), and a flag indicating whether the metric comes from the `detail` property (`isDetailMetric`).
     *
     */

    async onFireQuery (query, sortBy, isDetailMetric = false) {
      return this.fetchData({
        query,
        sortBy,
        isDetailMetric
      })
    },

    async fetchData (metric) {
      let response
      let queryId = this.queryId(metric)
      let queryUrl = this.queryUrl(metric)
      const placeHolders = metric.query?.sqlPlaceholders || {}
      let queryDataTable = this.queryDataTable(metric) // expecting the query datatable to be complete (ids etc)
      const options = {}
      if (metric.sortBy) {
        options.sortBy = metric.sortBy.map(s => s.field)
        options.sortDesc = metric.sortBy.map(s => s.order === 'desc')
      }
      // Build the API body using:
      // - datatable config of the metric
      // - searchText
      // - filterArray
      const body = Configuration.datatable.paginatedRequestBody(
        queryDataTable,
        options,
        this.context.searchTxt,
        this.context.filterArray,
        []
      )
      if (metric.isDetailMetric) {
        body.placeHolders = placeHolders
      }
      const config = this.$axios.agilisRequestConfig(queryUrl)
      this.startLoader({ id: queryId })
      try {
        response = (await this.$axios.post(
          queryUrl,
          body,
          config
        )).data
      } catch (error) {
        this.notifyError(error)
      } finally {
        this.stopLoader({ id: queryId })
      }
      return response
    },

    queryConfiguration (metric) {
      if (metric.api) {
        return metric.api
      } else {
        return metric.query || this.metrics.default?.query
      }
    },
    queryId (metric) {
      const query = this.queryConfiguration(metric)
      return query.id
    },
    queryUrl (metric) {
      const query = this.queryConfiguration(metric)
      if (query?.url) {
        return query.url
      }

      const queryId = this.queryId(metric)
      return this.$axios.agilisUrlQuery(this.applicationId, queryId)
    },

    queryDataTable (metric) {
      const queryId = this.queryId(metric)
      if (queryId === 'search') {
        return this.context?.dataTable
      }
      return Configuration.query.getQueryDatatable(this.applicationId, queryId)
    }
  },

  watch: {
    context (newContext, prevContext) {
      if (
        newContext.searchTxt !== prevContext.searchTxt ||
        JSON.stringify(newContext.filterArray) !== JSON.stringify(prevContext.filterArray)
      ) {
        this.fetchMetrics()
      }
    }
  }
}
